import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, Coffee, Sparkles, Radio, HeartHandshake, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://images.squarespace-cdn.com/content/v1/5fd42e869c252757ae0435b2/66f23618-666b-435c-8e09-4c16f41965ca/WMWE+Brunch+2023.png";

export default function WMWEGatherings() {
    return (
        <main data-testid="wmwe-gatherings" className="bg-sanct-sand text-sanct-charcoal">
            {/* Hero */}
            <section className="py-28 md:py-36 bg-sanct-cream text-center">
                <div className="max-w-[900px] mx-auto px-6 md:px-10">
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-6">Gatherings</p>
                    <h1 className="font-serif text-5xl md:text-7xl text-sanct-cocoa leading-[1] tracking-tight">
                        Where we meet,
                        <span className="block italic text-sanct-rose mt-2">worship, and grow.</span>
                    </h1>
                    <p className="font-sans text-base md:text-lg text-sanct-charcoal/75 mt-8 max-w-[640px] mx-auto leading-[1.8]">
                        Gatherings rooted in faith, sisterhood, and the truth that we were made for one another. Come sit at the table.
                    </p>
                </div>
            </section>

            {/* Next Up — RECON featured */}
            <section id="recon" className="py-24 md:py-32 bg-sanct-sand">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10">
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-3 text-center">Next Up</p>
                    <h2 className="font-serif text-4xl md:text-6xl text-sanct-cocoa leading-[1.05] tracking-tight text-center max-w-[900px] mx-auto">
                        <span className="italic text-sanct-rose">RECON.</span>
                        <span className="block">A gathering of intelligence.</span>
                    </h2>
                    <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        <div className="bg-sanct-cream rounded-[24px] p-10 md:p-12 border border-sanct-clay/40 shadow-sm">
                            <div className="flex items-center gap-3 mb-8">
                                <Radio size={22} className="text-sanct-rose" strokeWidth={1.5} />
                                <span className="font-sans text-xs uppercase tracking-[0.25em] text-sanct-cocoa">The Next Chapter</span>
                            </div>
                            <p className="font-serif text-xl md:text-2xl text-sanct-cocoa leading-[1.5]">
                                RECON is where women of purpose come together to <span className="italic">study, strategize, and stand watch</span> for one another.
                            </p>
                            <p className="font-sans text-base text-sanct-charcoal/80 mt-6 leading-[1.8]">
                                A gathering of intelligence &mdash; wisdom shared in trust, insight sharpened by sisterhood, prayer that goes deep. Not surface talk. Not networking. This is reconnaissance for the assignment on your life.
                            </p>
                            <div className="mt-8 pt-8 border-t border-sanct-clay/40 space-y-3">
                                <p className="font-sans text-sm text-sanct-cocoa/70">
                                    <span className="font-medium text-sanct-cocoa">Date &amp; Location:</span> Announced to the waitlist first.
                                </p>
                                <p className="font-sans text-sm text-sanct-cocoa/70">
                                    <span className="font-medium text-sanct-cocoa">Seats:</span> Intentionally limited.
                                </p>
                            </div>
                        </div>
                        <ReconWaitlist />
                    </div>
                </div>
            </section>

            {/* Hearts Held — recurring gathering */}
            <section className="py-24 md:py-32 bg-sanct-clay/30">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-12 items-center">
                        <div>
                            <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-4">Recurring</p>
                            <h2 className="font-serif text-4xl md:text-6xl text-sanct-cocoa leading-[1.05] tracking-tight">
                                <span className="italic text-sanct-rose">Hearts Held.</span>
                            </h2>
                            <p className="font-serif italic text-xl md:text-2xl text-sanct-cocoa/90 mt-8 leading-[1.5]">
                                A heart-centered evening of prayer, reflection, and sisterhood.
                            </p>
                            <p className="font-sans text-base text-sanct-charcoal/80 mt-6 leading-[1.8]">
                                An intimate gathering where we lay burdens down, lift each other up, and remember whose we are. Rooted in Scripture, held in prayer, softened by presence.
                            </p>
                            <p className="font-sans text-sm text-sanct-charcoal/70 mt-8 italic">
                                Dates announced to members first.
                            </p>
                        </div>
                        <div className="bg-sanct-cream rounded-[24px] p-8 md:p-10 border border-sanct-clay/40">
                            <div className="w-14 h-14 rounded-full bg-sanct-rose/20 flex items-center justify-center mb-6">
                                <HeartHandshake size={22} className="text-sanct-rose" strokeWidth={1.5} />
                            </div>
                            <blockquote className="font-serif italic text-2xl md:text-3xl text-sanct-cocoa leading-[1.4]">
                                &ldquo;Bear one another&apos;s burdens, and so fulfill the law of Christ.&rdquo;
                            </blockquote>
                            <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mt-6">Galatians 6:2</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Quarterly Experiences */}
            <section className="py-24 md:py-32 bg-sanct-sand">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="text-center max-w-[700px] mx-auto mb-14">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-5">Quarterly</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-sanct-cocoa leading-[1.1] tracking-tight">
                            In&#8209;person experiences that deepen the bond.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {[
                            { icon: Coffee, label: "Brunches" },
                            { icon: Sparkles, label: "Dinners" },
                            { icon: Calendar, label: "Retreats" },
                            { icon: Coffee, label: "Service Gatherings" },
                        ].map((q) => {
                            const Icon = q.icon;
                            return (
                                <div key={q.label} className="bg-sanct-cream rounded-[20px] p-10 text-center border border-sanct-clay/40">
                                    <div className="w-14 h-14 rounded-full bg-sanct-rose/20 flex items-center justify-center mx-auto mb-5">
                                        <Icon size={22} className="text-sanct-rose" strokeWidth={1.5} />
                                    </div>
                                    <h3 className="font-serif text-xl md:text-2xl text-sanct-cocoa leading-tight">{q.label}</h3>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Annual Gathering */}
            <section className="relative bg-sanct-cocoa overflow-hidden">
                <img src={HERO_IMG} alt="Annual gathering" className="w-full h-[60vh] object-cover opacity-50" />
                <div className="absolute inset-0 flex items-center justify-center p-6 bg-sanct-cocoa/40">
                    <div className="text-center max-w-[760px]">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-5">Annual</p>
                        <h2 className="font-serif text-4xl md:text-6xl text-sanct-cream leading-[1.05] tracking-tight">
                            The WMWE&reg; <span className="italic text-sanct-rose">Gathering.</span>
                        </h2>
                        <p className="font-serif italic text-xl md:text-2xl text-sanct-cream/85 mt-6 leading-[1.5]">
                            A sacred, in-person experience that anchors the year.
                        </p>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-28 md:py-36 bg-sanct-cream text-center">
                <div className="max-w-[800px] mx-auto px-6 md:px-10">
                    <h2 className="font-serif text-4xl md:text-6xl text-sanct-cocoa leading-[1.05] tracking-tight">
                        Reserve your seat.
                        <span className="block italic text-sanct-rose mt-2">Come gather with us.</span>
                    </h2>
                    <Link
                        to="/wmwe/fellowship#waitlist"
                        data-testid="wmwe-gatherings-register-btn"
                        className="inline-flex items-center gap-3 mt-12 px-9 py-5 bg-sanct-rose text-sanct-cream text-xs uppercase tracking-[0.25em] rounded-full hover:bg-sanct-cocoa transition-colors duration-300"
                    >
                        Reserve Your Seat
                    </Link>
                </div>
            </section>
        </main>
    );
}

function ReconWaitlist() {
    const [f, setF] = useState({ first_name: "", last_name: "", email: "", organization: "", notes: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!f.first_name || !f.email) {
            toast.error("Name and email required.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/events/waitlist`, {
                event_slug: "recon",
                event_name: "RECON",
                ...f,
            });
            setDone(true);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not add you to the waitlist.";
            toast.error(typeof msg === "string" ? msg : "Something went wrong.");
        }
        setSubmitting(false);
    };

    if (done) {
        return (
            <div className="bg-sanct-cocoa text-sanct-cream rounded-[24px] p-10 md:p-12 text-center" data-testid="recon-waitlist-success">
                <Radio size={28} className="mx-auto mb-6 text-sanct-rose" strokeWidth={1.5} />
                <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-4">You&apos;re on the list</p>
                <h3 className="font-serif text-3xl md:text-4xl leading-tight">
                    Watch your inbox.
                </h3>
                <p className="font-sans text-sm text-sanct-cream/75 mt-6 leading-[1.7]">
                    Dates, location, and details will land in your email before anyone else&apos;s. Thank you for showing up.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="bg-sanct-cocoa text-sanct-cream rounded-[24px] p-10 md:p-12" data-testid="recon-waitlist-form">
            <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-4">Waitlist</p>
            <h3 className="font-serif text-3xl md:text-4xl leading-tight">
                Get on the <span className="italic text-sanct-rose">list.</span>
            </h3>
            <p className="font-sans text-sm text-sanct-cream/75 mt-4 leading-[1.7]">
                Seats are limited and members hear first. Add your name and we&apos;ll reach out when doors open.
            </p>
            <div className="mt-8 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    <input
                        placeholder="First name"
                        required
                        value={f.first_name}
                        onChange={(e) => setF({ ...f, first_name: e.target.value })}
                        data-testid="recon-first-name"
                        className="w-full font-sans text-sm bg-transparent border-b border-sanct-cream/30 py-2.5 outline-none focus:border-sanct-rose placeholder:text-sanct-cream/40"
                    />
                    <input
                        placeholder="Last name"
                        value={f.last_name}
                        onChange={(e) => setF({ ...f, last_name: e.target.value })}
                        data-testid="recon-last-name"
                        className="w-full font-sans text-sm bg-transparent border-b border-sanct-cream/30 py-2.5 outline-none focus:border-sanct-rose placeholder:text-sanct-cream/40"
                    />
                </div>
                <input
                    type="email"
                    placeholder="Email address"
                    required
                    value={f.email}
                    onChange={(e) => setF({ ...f, email: e.target.value })}
                    data-testid="recon-email"
                    className="w-full font-sans text-sm bg-transparent border-b border-sanct-cream/30 py-2.5 outline-none focus:border-sanct-rose placeholder:text-sanct-cream/40"
                />
                <input
                    placeholder="Organization (optional)"
                    value={f.organization}
                    onChange={(e) => setF({ ...f, organization: e.target.value })}
                    data-testid="recon-organization"
                    className="w-full font-sans text-sm bg-transparent border-b border-sanct-cream/30 py-2.5 outline-none focus:border-sanct-rose placeholder:text-sanct-cream/40"
                />
                <textarea
                    placeholder="What are you praying about right now? (optional)"
                    rows={2}
                    value={f.notes}
                    onChange={(e) => setF({ ...f, notes: e.target.value })}
                    data-testid="recon-notes"
                    className="w-full font-sans text-sm bg-transparent border-b border-sanct-cream/30 py-2.5 outline-none focus:border-sanct-rose placeholder:text-sanct-cream/40 resize-none"
                />
            </div>
            <button
                type="submit"
                disabled={submitting}
                data-testid="recon-submit-btn"
                className="group mt-8 w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-sanct-rose text-sanct-cream text-xs uppercase tracking-[0.25em] rounded-full hover:bg-sanct-cream hover:text-sanct-cocoa transition-colors duration-300 disabled:opacity-50"
            >
                {submitting ? "Adding you\u2026" : "Add me to the waitlist"}
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </button>
        </form>
    );
}
